import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { environment } from 'environments/environment';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import * as Action from './actions';
import { Router } from '@angular/router';
import { ToastService } from 'app/services/toast.service';

@Injectable()
export class Effects {

  constructor(
    private actions$: Actions,
    private platform: Platform,
    private router: Router,
    private toastService: ToastService,
    private store: Store
  ) { }

  configure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.configure),
      switchMap(() => {
        if (!this.platform.is('capacitor')) {
          // Not native, maybe handle web fallback if needed or just ignore
          return of({ type: '[RevenueCat] Not Native' });
        }

        let apiKey = '';
        const env = environment as any;
        if (this.platform.is('ios')) {
          apiKey = env.revenueCat?.appleApiKey;
        } else if (this.platform.is('android')) {
          apiKey = env.revenueCat?.googleApiKey;
        }

        if (!apiKey) {
          return of(Action.loadPackagesFailure({ error: 'API Key not found' }));
        }

        return from(Purchases.configure({ apiKey })).pipe(
          switchMap(() => from(Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }))),
          tap(() => {
            Purchases.addCustomerInfoUpdateListener((info) => {
              this.store.dispatch(Action.updateCustomerInfo({ customerInfo: info }));
            });
          }),
          switchMap(() => from([
            Action.loadPackages(),
            Action.checkSubscriptionStatus()
          ])),
          catchError(error => of(Action.loadPackagesFailure({ error })))
        );
      })
    )
  );




  loadPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.loadPackages),
      switchMap(() =>
        from(Purchases.getOfferings()).pipe(
          map((offerings) => Action.loadPackagesSuccess({ offerings })),
          catchError(error => of(Action.loadPackagesFailure({ error })))
        )
      )
    )
  );

  purchasePackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.purchasePackage),
      switchMap(action =>
        from(Purchases.purchasePackage({ aPackage: action.package })).pipe(
          map(result => Action.purchasePackageSuccess({ customerInfo: result.customerInfo })),
          catchError(error => of(Action.purchasePackageFailure({ error })))
        )
      )
    )
  );

  restorePurchases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.restorePurchases),
      switchMap(() =>
        from(Purchases.restorePurchases()).pipe(
          map((result) => Action.restorePurchasesSuccess({ customerInfo: result.customerInfo })),
          catchError(error => of(Action.restorePurchasesFailure({ error })))
        )
      )
    )
  );

  checkSubscriptionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        Action.checkSubscriptionStatus,
      ),
      switchMap(() =>
        from(Purchases.getCustomerInfo()).pipe(
          map(({ customerInfo }) => Action.checkSubscriptionStatusSuccess({ customerInfo })),
          catchError(error => of(Action.checkSubscriptionStatusFailure({ error })))
        )
      )
    )
  );

  handleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.purchasePackageSuccess, Action.restorePurchasesSuccess),
      tap(({ customerInfo }) => {
        const env = environment as any;
        const entitlementId = env.revenueCat?.entitlementId;
        const active = customerInfo?.entitlements?.active;
        if (entitlementId && active?.[entitlementId]) {
          this.toastService.toastSuccess('Subscription active!');
          this.router.navigate(['/subscription']);
        } else if (customerInfo?.entitlements) {
          // check if any entitilement is active
          if (!active || Object.keys(active).length === 0) {
            this.toastService.warning('No active subscriptions found.', 3000);
          }
        }
      })
    ),
    { dispatch: false }
  );

  handleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        Action.purchasePackageFailure,
        Action.restorePurchasesFailure,
      ),
      tap(({ error }) => {
        const cancelled =
          error &&
          typeof error === 'object' &&
          (error as { userCancelled?: boolean }).userCancelled === true;
        if (!cancelled) {
          console.error('RevenueCat Error', error);
          this.toastService.error(error);
        }
      })
    ),
    { dispatch: false }
  );
}
